import React, { useRef, useEffect, useState } from "react";
import { Button, message } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { useTender } from "../../contexts/TenderContext";
import { useAuth } from "../../hooks/useAuth";
import { StepConfig } from "../../types/tender.types";
import { COLORS } from "../../utils/constants";
import { forceSaveBothBoqEndpoints } from "../../utils/boqVersionUtils";
import {
  useJobTypeVersionStatus,
  isVersionStatusInProgress,
} from "../../hooks/useJobTypeVersionStatus";
import { useGroundworksTotalCalculations } from "../../hooks/useGroundworksTotalCalculations";
import { useItemLibrary } from "../../hooks/useItemLibrary";
import { saveBothWallsBuildUp } from "../../utils/wallsBuildUpSave";
import { useQueryClient } from "@tanstack/react-query";
import { RETAINING_WALLS_BUILD_UP_QUERY_KEYS } from "../../hooks/useRetainingWallsBuildUp";
import { GEO_TOBER_WALL_BUILD_UP_QUERY_KEYS } from "../../hooks/useWallSectionBuildUp";
import { AppstoreOutlined } from "@ant-design/icons";

const LARGE_SCREEN_BREAKPOINT = 1400;

// Define workflow steps for each job type
const roadsAndSewersSteps: StepConfig[] = [
  {
    id: 0,
    label: "Preliminary Items",
    path: "/roads-sewers/preliminary",
    isComplete: false,
    isValid: false,
  },
  {
    id: 1,
    label: "Foul Sewers",
    path: "/roads-sewers/foul-sewers",
    isComplete: false,
    isValid: false,
  },
  {
    id: 2,
    label: "Storm Sewers",
    path: "/roads-sewers/storm-sewers",
    isComplete: false,
    isValid: false,
  },
  {
    id: 3,
    label: "Roads",
    path: "/roads-sewers/roads",
    isComplete: false,
    isValid: false,
  },
  {
    id: 4,
    label: "Main Services & Substation Base",
    path: "/roads-sewers/main-services-substation",
    isComplete: false,
    isValid: false,
  },
  {
    id: 5,
    label: "POS",
    path: "/roads-sewers/pos",
    isComplete: false,
    isValid: false,
  },
  {
    id: 6,
    label: "Spoil Reconciliation",
    path: "/roads-sewers/spoil-reconciliation",
    isComplete: false,
    isValid: false,
  },
  {
    id: 7,
    label: "Compound",
    path: "/roads-sewers/compound",
    isComplete: false,
    isValid: false,
  },
];

const groundworksSteps: StepConfig[] = [
  {
    id: 0,
    label: "Cut & Fill",
    path: "/groundworks/cut-fills",
    isComplete: false,
    isValid: false,
  },
  {
    id: 1,
    label: "Foundations",
    path: "/groundworks/foundations",
    isComplete: false,
    isValid: false,
  },
  {
    id: 2,
    label: "Drainage",
    path: "/groundworks/drainage",
    isComplete: false,
    isValid: false,
  },
  {
    id: 3,
    label: "Plot Drives",
    path: "/groundworks/plot-drives",
    isComplete: false,
    isValid: false,
  },
  {
    id: 4,
    label: "Plot Service",
    path: "/groundworks/plot-service",
    isComplete: false,
    isValid: false,
  },
  {
    id: 5,
    label: "Plot Paving",
    path: "/groundworks/plot-pavings",
    isComplete: false,
    isValid: false,
  },
  {
    id: 6,
    label: "Scaffold Platforms",
    path: "/groundworks/scaffold",
    isComplete: false,
    isValid: false,
  },
  {
    id: 7,
    label: "Garden",
    path: "/groundworks/garden",
    isComplete: false,
    isValid: false,
  },
  {
    id: 8,
    label: "Ret & Screen Walls",
    path: "/groundworks/retaining-walls",
    isComplete: false,
    isValid: false,
  },
  {
    id: 9,
    label: "Geowall & Tobermore Walls",
    path: "/groundworks/geo-tober-wall",
    isComplete: false,
    isValid: false,
  },
];

// Workflow routes that intentionally hide the stepper bar (mirrors the
// showStepper={false} pages previously passed into MainLayout)
const HIDDEN_STEPPER_PATHS = [
  "/roads-sewers/review",
  "/groundworks/review",
  "/groundworks/plot-matrix",
];

interface StepperNavigationProps {
  showSidebar?: boolean;
  sidebarWidth?: number;
}

const StepperNavigation: React.FC<StepperNavigationProps> = ({
  showSidebar = false,
  sidebarWidth = 220,
}) => {
  const { state, setWorkflowStep, saveTender, updateField } = useTender();
  const { user } = useAuth();
  const { data: itemLibraryData } = useItemLibrary(state.region || "");
  const matchedOpportunityNo = state.opportunityNo;
  // Job-type-scoped opportunity status - same shared hook/lookup Header.tsx
  // uses for the navbar opportunity Popover, so Calculate & Review's
  // skip-save check (below) always agrees with what the navbar shows.
  const { groundworksVersionStatus, roadsAndSewersVersionStatus } =
    useJobTypeVersionStatus(
      state.projectName || undefined,
      state.opportunityNo || undefined,
      !!state.opportunity?.groundworks,
      !!state.opportunity?.roadsAndSewers,
    );
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const activeStepRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [lineWidth, setLineWidth] = useState(120);

  // Read checkbox states from localStorage on every render (same pattern as CutFills)
  const getCheckboxState = (key: string, fallbackPercent: number): boolean => {
    const saved = localStorage.getItem(key);
    if (saved !== null) return JSON.parse(saved);
    return fallbackPercent > 0;
  };

  const preliminariesPercent = (state.globalSettings as any)?.groundworks?.preliminaries || 0;
  const ohpPercent = (state.globalSettings as any)?.groundworks?.overheadAndProfit || 0;
  const discountPercent = (state.globalSettings as any)?.groundworks?.discount || 0;

  const includePreliminaries = getCheckboxState("groundworks_includePreliminariesDisplay", preliminariesPercent);
  const includeOhp = getCheckboxState("groundworks_includeOhpDisplay", ohpPercent);
  const includeDiscount = getCheckboxState("groundworks_includeDiscountDisplay", discountPercent);

  // Always call hook (React rules) — only used when workflowType === "groundworks"
  const groundworksTotals = useGroundworksTotalCalculations({
    includePreliminaries,
    includeOhp,
    includeDiscount,
  });

  const getCurrentWorkflow = () => {
    const path = location.pathname;
    if (path.startsWith("/roads-sewers/")) {
      return { type: "roadsAndSewers" as const, steps: roadsAndSewersSteps };
    } else if (path.startsWith("/groundworks/")) {
      return { type: "groundworks" as const, steps: groundworksSteps };
    }
    return null;
  };

  const currentWorkflow = getCurrentWorkflow();

  // Calculate dynamic line width for large screens
  useEffect(() => {
    const calculateLineWidth = () => {
      if (!containerRef.current || !currentWorkflow) return;

      const containerWidth = containerRef.current.offsetWidth;
      const steps = currentWorkflow.steps;
      const numLines = steps.length - 1;
      const totalDotWidth = steps.length * 14 + 8; // approximate dot widths
      const padding = 70; // left + right padding
      const buttonSpace = 200; // space for button
      const minLineWidth = 170; // minimum to fit longest labels

      if (window.innerWidth >= LARGE_SCREEN_BREAKPOINT && numLines > 0) {
        const availableWidth =
          containerWidth - totalDotWidth - padding - buttonSpace;
        const calculatedWidth = Math.max(
          minLineWidth,
          Math.floor(availableWidth / numLines),
        );
        setLineWidth(calculatedWidth);
      } else {
        setLineWidth(minLineWidth);
      }
    };

    calculateLineWidth();
    window.addEventListener("resize", calculateLineWidth);
    return () => window.removeEventListener("resize", calculateLineWidth);
  }, [currentWorkflow]);

  useEffect(() => {
    const scrollToActive = () => {
      if (activeStepRef.current && scrollContainerRef.current) {
        const container = scrollContainerRef.current;
        const activeElement = activeStepRef.current;
        const containerRect = container.getBoundingClientRect();
        const activeRect = activeElement.getBoundingClientRect();
        const scrollLeft =
          activeRect.left -
          containerRect.left -
          containerRect.width / 2 +
          activeRect.width / 2 +
          container.scrollLeft;
        container.scrollTo({ left: Math.max(0, scrollLeft), behavior: "smooth" });
      }
    };

    // Delay to ensure DOM layout (including lineWidth re-render) has settled
    const timer = setTimeout(scrollToActive, 100);
    return () => clearTimeout(timer);
  }, [location.pathname, lineWidth]);

  if (!currentWorkflow || HIDDEN_STEPPER_PATHS.includes(location.pathname)) {
    return null;
  }

  const { type: workflowType, steps } = currentWorkflow;
  const currentStepIndex = steps.findIndex(
    (step) => step.path === location.pathname,
  );
  const activeStep =
    currentStepIndex >= 0
      ? currentStepIndex
      : state.workflow[workflowType].currentStep;
  // Allow skipping steps regardless of status
  const canSkipSteps = true;

  const handleStepClick = (stepIndex: number) => {
    setWorkflowStep(workflowType, stepIndex);
    navigate(steps[stepIndex].path);
  };

  const handleCalculateReview = async () => {
    // In view-only mode (historical version), skip all saves and navigate directly
    const isHistoricalVersion = localStorage.getItem("isHistoricalVersion") === "true";
    if (isHistoricalVersion) {
      navigate(
        workflowType === "groundworks"
          ? "/groundworks/review"
          : "/roads-sewers/review",
      );
      return;
    }

    // Current job type's opportunity resolved to a definite non-"In-progress"
    // status (e.g. already submitted/under review/approved) - navigate
    // straight to review without saving, same as the historical-version case
    // above. isVersionStatusInProgress treats `undefined` (status not
    // loaded yet, or no version exists yet e.g. a brand-new tender) as "in
    // progress" too, so an unresolved status still saves normally, and it
    // compares case-insensitively since both "In-progress" and
    // "In-Progress" get written as real values elsewhere in the app.
    const activeVersionStatus =
      workflowType === "roadsAndSewers"
        ? roadsAndSewersVersionStatus
        : groundworksVersionStatus;
    if (!isVersionStatusInProgress(activeVersionStatus)) {
      navigate(
        workflowType === "roadsAndSewers"
          ? "/roads-sewers/review"
          : "/groundworks/review",
      );
      return;
    }

    message.loading({ content: "Saving tender...", key: "calcReviewSave", duration: 0 });
    try {
      const prelimsAmount = groundworksTotals.sections.reduce((sum, s) => sum + s.preliminariesAmount, 0);
      const ohpAmount = groundworksTotals.sections.reduce((sum, s) => sum + s.ohpAmount, 0);

      if (workflowType === "groundworks") {
        // Update tender state with hook-calculated totals (exact same as CutFills pattern)
        updateField("groundworks", {
          ...state.groundworks,
          adjustments: {
            preliminaries: prelimsAmount,
            ohp: ohpAmount,
            discount: groundworksTotals.discountAmount,
            includePreliminaries: groundworksTotals.preliminariesPercent > 0,
            includeOhp: groundworksTotals.ohpPercent > 0,
            includeDiscount: groundworksTotals.discountPercent > 0,
          },
          totals: {
            ...state.groundworks?.totals,
            grandTotal: groundworksTotals.grandTotal,
            totalWithAdjustments: groundworksTotals.tenderTotal,
            tenderTotal: groundworksTotals.tenderTotal,
          },
        });

        updateField("globalSettings", {
          ...state.globalSettings,
          groundworks: {
            overheadAndProfit: groundworksTotals.ohpPercent,
            preliminaries: groundworksTotals.preliminariesPercent,
            discount: groundworksTotals.discountPercent,
          },
        });
      }

      updateField("status", "In-progress");
      updateField("boqStatus", "In-progress");
      updateField("lastModified", new Date());

      await saveTender();
      await new Promise((resolve) => setTimeout(resolve, 200));

      const totalsToPass = workflowType === "groundworks"
        ? {
            grandTotal: groundworksTotals.grandTotal,
            grandTotalWithAdjustments: groundworksTotals.grandTotalWithAdjustments,
            preliminariesAmount: prelimsAmount,
            ohpAmount: ohpAmount,
            discountAmount: groundworksTotals.discountAmount,
            tenderTotal: groundworksTotals.tenderTotal,
            preliminariesPercent: groundworksTotals.preliminariesPercent,
            ohpPercent: groundworksTotals.ohpPercent,
            discountPercent: groundworksTotals.discountPercent,
          }
        : undefined;

      // `state` here is still the closure captured at render time — the updateField
      // calls above dispatch asynchronously and don't flow back into this component's
      // `state` variable until the next render, so a stale status (e.g. "submitted"
      // left over from a prior Submit Tender) would otherwise get re-persisted as-is
      // instead of "In-progress". Build the payload from an explicit merged copy.
      const stateToSave = {
        ...state,
        status: "In-progress" as const,
        boqStatus: "In-progress" as const,
        lastModified: new Date(),
      };

      // Save BOQ versions first to establish the correct version number
      const { version } = await forceSaveBothBoqEndpoints(stateToSave, "Save and continue", totalsToPass, user?.name, itemLibraryData, matchedOpportunityNo);

      // Match the on-change save path used by WallsSpecificationContainer
      // and GeoToberWallSpecificationContainer: always push current state
      // to the dedicated walls APIs. The previous `version >= 2 →
      // copyWallsBuildUpFromPreviousVersion` branch was overwriting the
      // user's edits with v(n-1)'s data on every Calculate Review at
      // version 2+, so the walls API ended up holding the previous
      // version's specs instead of what the user just typed.
      await saveBothWallsBuildUp({ tender: state, itemLibraryData, ohpPercent: groundworksTotals.ohpPercent, prelimsPercent: groundworksTotals.preliminariesPercent, versionOverride: version });

      // Mirror the on-change save: direct API POSTs bypass the mutation
      // hooks' onSuccess invalidation, so refresh both caches manually.
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: RETAINING_WALLS_BUILD_UP_QUERY_KEYS.detail(state.projectName, version),
        }),
        queryClient.invalidateQueries({
          queryKey: GEO_TOBER_WALL_BUILD_UP_QUERY_KEYS.detail(state.projectName, version),
        }),
      ]);

      // No success toast — only surface an alert when the save actually fails.
      message.destroy("calcReviewSave");
    } catch (error) {
      console.error("Error saving before review:", error);
      message.error({ content: "Failed to save tender", key: "calcReviewSave", duration: 3 });
    }
    navigate(
      workflowType === "groundworks"
        ? "/groundworks/review"
        : "/roads-sewers/review",
    );
  };

  return (
    <div
      ref={containerRef}
      style={{
        position: "fixed",
        top: "88px",
        left: showSidebar ? `${sidebarWidth}px` : "0",
        right: "0",
        width: showSidebar ? `calc(100% - ${sidebarWidth}px)` : "100%",
        backgroundColor: "#F1F3F9",
        zIndex: 99,
        transition: "left 0.2s ease, width 0.2s ease",
        display: "flex",
        alignItems: "center",
        padding: "12px 24px",
        gap: "20px",
      }}
    >
      {/* Scrollable Steps Container */}
      <div
        ref={scrollContainerRef}
        className="stepper-scroll-container"
        style={{
          flex: 1,
          overflowX: "auto",
          overflowY: "hidden",
          minWidth: 0,
        }}
      >
        <div style={{ padding: "8px 20px 8px 50px", display: "inline-block" }}>
          {/* Dots and Lines Row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            {steps.map((step, index) => {
              const isActive = index === activeStep;
              const isCompleted = index < activeStep;
              const isLast = index === steps.length - 1;
              const isDisabled = !canSkipSteps && index > activeStep + 1;

              return (
                <React.Fragment key={step.id}>
                  {/* Dot */}
                  <div
                    ref={isActive ? activeStepRef : null}
                    onClick={() => !isDisabled && handleStepClick(index)}
                    style={{
                      width: isActive ? "22px" : "14px",
                      height: isActive ? "22px" : "14px",
                      borderRadius: "50%",
                      backgroundColor: isActive
                        ? COLORS.primary
                        : isCompleted
                          ? COLORS.primary
                          : "transparent",
                      border:
                        isActive || isCompleted ? "none" : "2px solid #C5CAD3",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      boxShadow: isActive
                        ? "0 0 0 4px rgba(37, 99, 235, 0.15)"
                        : "none",
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      opacity: isDisabled ? 0.5 : 1,
                    }}
                  >
                    {isActive && (
                      <div
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor: "white",
                        }}
                      />
                    )}
                  </div>

                  {/* Connector Line */}
                  {!isLast && (
                    <div
                      style={{
                        width: `${lineWidth}px`,
                        height: "2px",
                        backgroundColor: isCompleted
                          ? COLORS.primary
                          : "#C5CAD3",
                        flexShrink: 0,
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Labels Row */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              marginTop: "8px",
            }}
          >
            {steps.map((step, index) => {
              const isActive = index === activeStep;
              const isDisabled = !canSkipSteps && index > activeStep + 1;
              const isLast = index === steps.length - 1;

              // Calculate label width: dot width + line width (except for last)
              const dotWidth = isActive ? 22 : 14;
              const currentLineWidth = isLast ? 0 : lineWidth;
              const totalWidth = dotWidth + currentLineWidth;

              return (
                <div
                  key={step.id}
                  onClick={() => !isDisabled && handleStepClick(index)}
                  style={{
                    width: `${totalWidth}px`,
                    flexShrink: 0,
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    opacity: isDisabled ? 0.5 : 1,
                    position: "relative",
                    height: "20px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "13px",
                      fontWeight: isActive ? 600 : 400,
                      color: isActive ? COLORS.primary : "#64748B",
                      whiteSpace: "nowrap",
                      position: "absolute",
                      left: `${dotWidth / 2}px`,
                      transform: "translateX(-50%)",
                    }}
                  >
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Calculate & Review Button */}
      <Button
        type="primary"
        icon={<AppstoreOutlined />}
        onClick={handleCalculateReview}
        style={{
          backgroundColor: "#D0EEFE",
          borderColor: "#D0EEFE",
          color: COLORS.primary,
          height: "42px",
          padding: "0 20px",
          fontWeight: 500,
          fontSize: "14px",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: "8px",
          borderRadius: "6px",
        }}
      >
        Calculate & review
      </Button>

      <style>{`
        .stepper-scroll-container::-webkit-scrollbar {
          height: 4px;
        }
        .stepper-scroll-container::-webkit-scrollbar-track {
          background: transparent;
        }
        .stepper-scroll-container::-webkit-scrollbar-thumb {
          background: #CBD5E1;
          border-radius: 2px;
        }
        .stepper-scroll-container::-webkit-scrollbar-thumb:hover {
          background: #94A3B8;
        }
      `}</style>
    </div>
  );
};

export default StepperNavigation;
